import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Debounced saving with a hard latency cap.
 *
 * The legacy tool POSTed the entire 386 KB annotation map on every keystroke. This
 * coalesces edits into one request per sample, but `maxWait` guarantees a save still
 * lands within a few seconds of the first change, so continuous typing cannot defer a
 * save indefinitely.
 *
 * status: idle | dirty | saving | saved | error
 */
export function useDebouncedSave(saveFn, { delay = 800, maxWait = 5000 } = {}) {
  const [state, setState] = useState({ status: 'idle', at: null, error: null });

  const timer = useRef(null);
  const firstDirtyAt = useRef(0);
  const pending = useRef(null);
  const inFlight = useRef(false);
  const saveRef = useRef(saveFn);
  saveRef.current = saveFn;

  const flush = useCallback(async () => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    if (pending.current == null || inFlight.current) return;

    const payload = pending.current;
    pending.current = null;
    firstDirtyAt.current = 0;
    inFlight.current = true;
    setState((s) => ({ ...s, status: 'saving' }));

    try {
      await saveRef.current(payload);
      setState({
        status: pending.current ? 'dirty' : 'saved',
        at: Date.now(),
        error: null,
      });
    } catch (error) {
      // Keep the payload so a manual retry has something to send.
      pending.current = payload;
      setState({ status: 'error', at: Date.now(), error });
    } finally {
      inFlight.current = false;
    }
  }, []);

  const schedule = useCallback(() => {
    const now = Date.now();
    if (!firstDirtyAt.current) firstDirtyAt.current = now;
    if (timer.current) clearTimeout(timer.current);
    const wait = Math.min(delay, Math.max(0, firstDirtyAt.current + maxWait - now));
    timer.current = setTimeout(flush, wait);
  }, [delay, maxWait, flush]);

  const queue = useCallback(
    (payload) => {
      pending.current = payload;
      setState((s) => ({ ...s, status: 'dirty' }));
      schedule();
    },
    [schedule],
  );

  const retry = useCallback(() => {
    if (pending.current != null) flush();
  }, [flush]);

  useEffect(() => {
    const onHide = () => {
      if (document.visibilityState === 'hidden') flush();
    };
    document.addEventListener('visibilitychange', onHide);
    return () => {
      document.removeEventListener('visibilitychange', onHide);
      flush();
    };
  }, [flush]);

  return {
    ...state,
    queue,
    flush,
    retry,
    hasPending: () => pending.current != null,
  };
}
